import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Trash2 } from 'lucide-react';

import uploadIcon from 'assets/images/container.svg';
import background from 'assets/images/background.svg';
import { useTheme } from 'components/core/theme-provider';
import gdriveIcon from 'assets/images/gdrive.svg';
import onedriveIcon from 'assets/images/onedrive.svg';
import sharepointIcon from 'assets/images/sharepoint.svg';
import { useCustomGptStore } from 'state/store/chat/use-custom-gpt-store';
import { useDeleteCustomGptFile } from '../../hooks/use-custom-gpt';
import { Loader2 } from 'lucide-react';


export const KnowledgeBaseTab = () => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const { localFiles, files, customGptId, addLocalFile, removeLocalFile, removeFile: removeServerFile } = useCustomGptStore();
    const { mutate: deleteFile, isPending: isDeletingFile } = useDeleteCustomGptFile();
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            Array.from(e.target.files).forEach(file => {
                addLocalFile(file);
            });
        }
    };

    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="flex flex-col gap-6">
            {/* Upload Area */}
            <div className={`w-full p-10 inline-flex items-center justify-center gap-6 rounded-[8px] border-[1px] border-dashed ${theme === 'dark' ? "bg-black-200 border-black-500" : "bg-typography-white border-neutral-200"}`}>
                <div className="relative w-[84px] h-[63px] shrink-0">
                    {/* Background */}
                    <img
                        src={background}
                        alt="Background"
                        className="absolute inset-0 w-full h-full opacity-20"
                    />

                    {/* Upload Icon */}
                    <img
                        src={uploadIcon}
                        alt="Upload"
                        className="absolute inset-0 w-full h-full z-10"
                    />
                </div>

                <div className="flex flex-col items-start gap-3">
                    <div className="flex flex-col gap-2">
                        <p className={`${theme === 'dark' ? "text-typography-white" : "text-[#212B36]"} text-sm font-inter font-medium leading-[22px]`}>
                            {t('UPLOAD_FILES_TITLE') || 'Upload files or connect integrations'}
                        </p>
                        <p className={`${theme === 'dark' ? "text-typography-white-stroke" : "text-[#637381]"} text-xs font-inter font-normal leading-[18px]`}>
                            {t('UPLOAD_FILES_DESC') || 'PDF, DOCX, TXT (Max 25MB per file)'}
                        </p>
                    </div>

                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className={`${theme === 'dark' ? "bg-primary-600 text-typography-white" : "bg-primary-600 text-typography-white"} px-3 py-[6px] rounded-[6px] text-sm font-inter font-semibold leading-[24px]`}>
                        {t('BROWSE_FILES') || 'Browse Files'}
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        className="hidden"
                        accept=".pdf,.docx,.txt,.md"
                    />
                </div>
            </div>


            {/* Integrations */}
            <div className="flex gap-2">
                {[
                    { id: 'google-drive', name: t('GOOGLE_DRIVE'), icon: gdriveIcon },
                    { id: 'share-point', name: t('SHARE_POINT'), icon: sharepointIcon },
                    { id: 'onedrive', name: t('ONEDRIVE'), icon: onedriveIcon },
                ].map((integ) => (
                    <button disabled={true} key={integ.id} className="w-full h-full p-4 bg-white rounded-lg border border-[0.5px] border-[#BCBCBE] flex flex-col items-center justify-start gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                        <img src={integ.icon} alt={integ.name} className={`w-6 h-6`} />
                        <span className={`${theme === 'dark' ? "text-typography-white" : "text-[#816C51]"} text-xs font-normal leading-[18px] break-words font-inter`}>{integ.name}</span>
                    </button>
                ))}
            </div>

            {/* Knowledge Base List */}
            <div>
                <h4 className={`${theme === 'dark' ? "text-typography-white" : "text-black-500"} text-sm font-medium leading-[22px] break-words font-inter mb-3`}>
                    {t('KNOWLEDGE_BASE_COUNT', { count: localFiles.length + files.length }) || `Knowledge Base (${localFiles.length + files.length} files)`}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Server Files */}
                    {files.map((file) => (
                        <div key={file.fileId} className={`flex items-center justify-between p-3 rounded-lg border group ${theme === 'dark' ? "bg-black-300 border-[#AC9578]" : "bg-[#FFF0D040] border-[#AC9578]"}`}>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-white/10 rounded flex items-center justify-center">
                                    <FileText className={`w-4 h-4 ${theme === 'dark' ? "text-[#AC9578]" : "text-[#816C51]"}`} />
                                </div>
                                <div className="flex flex-col">
                                    <span className={`${theme === 'dark' ? "text-typography-white" : "text-black-500"} text-sm font-medium truncate max-w-[140px]`}>{file.fileName}</span>
                                    <span className={`${theme === 'dark' ? "text-typography-grey-2-secondary-stroke" : "text-typography-grey-1-secondary"} text-[10px] text-gray-500`}>{formatSize(file.size)} | {t('SERVER') || 'Server'}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    if (customGptId) {
                                        deleteFile({ customGptId, fileId: file.fileId }, {
                                            onSuccess: () => removeServerFile(file.fileId)
                                        });
                                    }
                                }}
                                disabled={isDeletingFile}
                                className="p-1.5 text-[#FF3B3B] disabled:opacity-50">
                                {isDeletingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                        </div>
                    ))}

                    {/* Local Files */}
                    {localFiles.map((file, idx) => (
                        <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border group ${theme === 'dark' ? "bg-black-300 border-[#AC9578]" : "bg-[#FFF0D040] border-[#AC9578]"}`}>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-white/10 rounded flex items-center justify-center">
                                    <FileText className={`w-4 h-4 ${theme === 'dark' ? "text-[#AC9578]" : "text-[#816C51]"}`} />
                                </div>
                                <div className="flex flex-col">
                                    <span className={`${theme === 'dark' ? "text-typography-white" : "text-black-500"} text-sm font-medium truncate max-w-[140px]`}>{file.name}</span>
                                    <span className={`${theme === 'dark' ? "text-typography-grey-2-secondary-stroke" : "text-typography-grey-1-secondary"} text-[10px] text-gray-500`}>{formatSize(file.size)} | {t('LOCAL') || 'Local'}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => removeLocalFile(idx)}
                                className="p-1.5 text-[#FF3B3B]">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div >
    );
};
